import React, { useEffect } from 'react';
import { Banner } from '../../components/Banner';
import { Button, Checkbox, ProgressPolygonList } from '@silverfort/sf-components';
import { SoftwareStatus } from '../../types';
import { httpPost } from '../../../../utils';

const UpdateAdminConsoleStep: React.FC<{ softwareStatus: SoftwareStatus; handleSendLogs: () => void }> = ({
    softwareStatus,
    handleSendLogs
}) => {
    const [confirmationChecked, setConfirmationChecked] = React.useState<boolean>(
        softwareStatus.upgradingTower || softwareStatus.towerUpgradeProgress === 100
    );
    const [isUpgrading, setIsUpgrading] = React.useState<boolean>(softwareStatus.upgradingTower);
    const [upgradeProcess, setUpgradeProcess] = React.useState<number>(softwareStatus.towerUpgradeProgress || 0);
    const [upgradeCompleted, setUpgradeCompleted] = React.useState<boolean>(
        softwareStatus.currentTowerVersion ===
            (softwareStatus.versionUpgradeInProgress || softwareStatus.maxTowerVersion) &&
            !softwareStatus.upgradingTower
    );
    const [logsSent, setLogsSent] = React.useState<boolean>(false);

    const handleAdminCConsoleUpgrade = async () => {
        setIsUpgrading(true);
        setUpgradeProcess(0);
        try {
            await httpPost(`/softwareUpdates/upgradeTower`);
        } catch (error) {
            console.error(error);
            setIsUpgrading(false);
            setUpgradeProcess(0);
        }
    };

    useEffect(() => {
        if (softwareStatus.towerUpgradeError) {
            setIsUpgrading(false);
            setUpgradeProcess(0);
            setUpgradeCompleted(false);
            console.log('Admin Console upgrade error:', softwareStatus.towerUpgradeError);
        } else {
            setIsUpgrading(softwareStatus.upgradingTower);
            setUpgradeProcess(softwareStatus.towerUpgradeProgress || 0);
            setUpgradeCompleted(
                softwareStatus.currentTowerVersion === softwareStatus.maxTowerVersion && !softwareStatus.upgradingTower
            );
        }
    }, [softwareStatus]);

    return (
        <>
            {upgradeCompleted && (
                <Banner theme="Success">
                    <Banner.Body content={'Admin Console has been upgraded successfully.'} />
                </Banner>
            )}
            {softwareStatus.towerUpgradeError && (
                <Banner
                    theme="Critical"
                    actionButtonProps={{
                        text: 'Send Logs',
                        onClick: () => {
                            setLogsSent(true);
                            handleSendLogs();
                        },
                        disabled: logsSent
                    }}>
                    <Banner.Body
                        content={
                            'Something went wrong during the Admin Console upgrade. Download logs to investigate, or send logs and contact support for assistance.'
                        }
                    />
                </Banner>
            )}
            <div className="flex flex-col gap-5">
                <div className="p-4 flex flex-col gap-2">
                    <div className="font-medium text-18 font-sf-poppins text-sf-gray-800">Update Admin Console</div>
                    <div className="text-13 text-sf-gray-500 font-sf">
                        Before starting the upgrade, we recommend creating a backup snapshot of your Admin Console. This
                        will let you roll back quickly if any issues occur. The Admin Console will restart during the
                        upgrade.
                    </div>
                </div>
                {!upgradeCompleted && !isUpgrading && (
                    <div>
                        <Banner theme="Attention" hideIcon>
                            <Checkbox
                                checked={confirmationChecked}
                                onChange={(e) => setConfirmationChecked(e.target.checked)}
                            />
                            <Banner.Body content="I confirm that I’ve created a backup snapshot of the Admin Console (recommended for rollback)" />
                        </Banner>
                    </div>
                )}
                <div className="h-8 mt-10">
                    {isUpgrading && (
                        <div>
                            <ProgressPolygonList
                                isAnimationActive={false}
                                items={[
                                    {
                                        label: `${upgradeProcess}% Completed`,
                                        progress: upgradeProcess,
                                        endColor: '#E9F5FF',
                                        startColor: '#188DED'
                                    }
                                ]}
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-end mt-10">
                    <Button
                        text={'Upgrade Admin Console'}
                        disabled={!confirmationChecked || isUpgrading || upgradeCompleted}
                        onClick={() => {
                            handleAdminCConsoleUpgrade();
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default UpdateAdminConsoleStep;
